import React from "react";
import { cn } from "@/lib/utils";

/**
 * ZenMe wordmark — user-provided mark image + ZENME wordmark.
 *
 * The mark file is the user's uploaded brand asset (rounded-square container
 * with an angular Z-motif) rendered from `/brand/zenme-mark*.png`. We do NOT
 * re-draw it inline; the file is served as a static asset and swapped by
 * `variant` prop.
 *
 * Props:
 *   variant: "default" | "mono-white" | "mono-emerald"
 *     - default        → emerald mark  + white  ZENME
 *     - mono-white     → white   mark  + white  ZENME
 *     - mono-emerald   → emerald mark  + emerald ZENME
 *   size: "sm" | "md" | "lg" — presets for rendered pixel height (defaults to "sm")
 *   height: number — explicit override in px (wins over `size`)
 *   showWordmark: boolean — set false to render just the mark (default true)
 */
const SIZE_MAP = { sm: 40, md: 56, lg: 72 };

const MARK_SRC = {
  default: "/brand/zenme-mark.png",
  "mono-white": "/brand/zenme-mark-white.png",
  "mono-emerald": "/brand/zenme-mark-emerald.png",
};

export const Logo = React.forwardRef(function Logo(
  {
    variant = "default",
    size = "sm",
    height,
    showWordmark = true,
    className,
    ...props
  },
  ref,
) {
  const targetH = height ?? SIZE_MAP[size] ?? SIZE_MAP.sm;
  const wordColor = variant === "mono-emerald" ? "#00E676" : "#FFFFFF";
  const src = MARK_SRC[variant] ?? MARK_SRC.default;

  // Wordmark is drawn in SVG (Space Grotesk 700, uppercase) so it scales
  // crisply next to the raster mark and shares the same baseline.
  const wordmarkSvg = showWordmark ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 136 48"
      width={(targetH / 48) * 136}
      height={targetH}
      aria-hidden="true"
      className="block"
    >
      <text
        x="0"
        y="24"
        fontFamily='"Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif'
        fontSize="26"
        fontWeight="700"
        fill={wordColor}
        letterSpacing="2.4"
        dominantBaseline="central"
      >
        ZENME
      </text>
    </svg>
  ) : null;

  return (
    <span
      ref={ref}
      role="img"
      aria-label="ZenMe Capital"
      className={cn("inline-flex select-none items-center gap-2", className)}
      style={{ height: targetH }}
      {...props}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        width={targetH}
        height={targetH}
        draggable="false"
        className="block"
        style={{ width: targetH, height: targetH, objectFit: "contain" }}
      />
      {wordmarkSvg}
    </span>
  );
});

export default Logo;
